import { useQuery } from '@tanstack/react-query'

import { fetchBranding, type BrandingResponseType } from './api.ts'
import { useAuth } from './auth-context.ts'

/**
 * A marca desta loja (F169).
 *
 * ## Uma consulta só, compartilhada
 *
 * `queryKey` com o tenant dentro: o menu lateral, a tela de Aparência e o
 * provedor de cor perguntam a mesma coisa e o cache responde uma vez. Trocar de
 * loja troca a chave, e a marca da loja anterior não fica na tela da seguinte.
 *
 * ## Não pergunta quando a chave está desligada
 *
 * `enabled` olha `hasFeature`, que vem da sessão. Sem isso, toda loja do parque
 * — a esmagadora maioria, que não tem marca própria — faria uma requisição por
 * carregamento para receber `null`.
 */
export const useBranding = (): {
  logoUrl: string | null
  primaryColor: string | null
  data: BrandingResponseType | undefined
  isLoading: boolean
} => {
  const { session, hasFeature } = useAuth()
  const tenantId = session?.context?.tenantId ?? null
  const enabled = tenantId !== null && hasFeature('marca-propria')

  const query = useQuery({
    queryKey: ['branding', tenantId],
    queryFn: ({ signal }) => fetchBranding(signal),
    enabled,
  })

  const branding = query.data?.branding ?? null
  const version = query.data?.version ?? null

  return {
    /*
     * A URL carrega a versão porque a resposta é `immutable` por um ano: sem
     * ela, quem trocou a logo continuaria vendo a antiga até limpar o cache.
     */
    logoUrl:
      branding?.logoKey === null || branding?.logoKey === undefined
        ? null
        : `/api/v1/organization/branding/logo?v=${String(version ?? 0)}`,
    primaryColor: branding?.primaryColor ?? null,
    data: query.data,
    isLoading: query.isLoading,
  }
}
